/* ---------------------------------------------------------------------------
   A photograph you can get close to.

   The enlarged view used to be an <img> in a box, which is enough to see that a
   pier is spalled and not enough to see how deep. A crack photographed at 12
   megapixels carries the answer; a 700px frame throws it away. So the frame is
   as large as the window allows and the photograph inside it zooms — the wheel,
   the toolbar, a double-click, +/- and the arrow keys — up to eight times the
   whole-photograph view, and never below it.

   ZOOM IS RELATIVE TO THE WHOLE PHOTOGRAPH IN FRAME, and 1× is that view. It is
   not a percentage of the file's own pixels, because the same 1× is a different
   number of pixels on every window; the "1:1" button is what actual pixels are
   reached by, and it disables itself on a photograph small enough that the fitted
   view already IS its own pixels.

   State — the zoom and the pan — lives here rather than in the store, because
   a wheel notch is not a fact about the register and must not repaint the screen
   behind the dialog. It survives repaints all the same: a repaint rebuilds this
   tree and the reconciler copies the new transform onto the live image, so the
   values only have to be read back out of this module. It resets when the dialog
   is closed and opened again, which is what a fresh look at a photograph means —
   detected by whether a stage is on the document at build time, since a repaint
   has one and an opening dialog does not.

   Nothing here closes over the elements it acts on. render() patches the new
   tree's handlers onto the element ALREADY in the document and throws the new
   element away, so a captured reference is a detached node one repaint later —
   the same trap src/components/imageSlot.ts documents. Everything is reached
   through the event instead.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type IconPath } from '../core/dom.ts';

/** Fit — the whole photograph in frame. There is nothing below it to see. */
const MIN_Z = 1;
const MAX_Z = 8;
/** One toolbar press, or one notch of a wheel that reports in notches. */
const STEP = 1.4;
/** One arrow press, in screen pixels — the keyboard's answer to dragging. */
const PAN_KEY = 56;

type View = { src: string; z: number; tx: number; ty: number };

let view: View = { src: '', z: MIN_Z, tx: 0, ty: 0 };
let drag: { id: number; x: number; y: number } | null = null;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** The stage the event happened in, whatever inside it was clicked. */
function stageOf(e: Event): HTMLElement | null {
  const from = e.currentTarget as HTMLElement | null;
  return from?.closest<HTMLElement>('.photolb__stage') ?? null;
}

function imageIn(stage: HTMLElement): HTMLImageElement | null {
  return stage.querySelector<HTMLImageElement>('.photolb__img');
}

/**
 * How far the photograph may be dragged before its edge would come inside the
 * frame, in each direction.
 *
 * Measured, not derived: the fitted size is whatever CSS gave the image, and the
 * frame is whatever the window left for it. Zero on an axis the zoomed image does
 * not overflow — nothing to pan there, and the transform keeps it centred.
 */
function panLimit(stage: HTMLElement, img: HTMLImageElement, z: number): { x: number; y: number } {
  return {
    x: Math.max(0, (img.clientWidth * z - stage.clientWidth) / 2),
    y: Math.max(0, (img.clientHeight * z - stage.clientHeight) / 2),
  };
}

/** The zoom at which the photograph is shown at its own pixels, or null if it never is. */
function oneToOne(img: HTMLImageElement): number | null {
  if (!img.naturalWidth || !img.clientWidth) return null;
  const z = img.naturalWidth / img.clientWidth;
  // Already there: a photograph smaller than its frame is fitted at 1:1 exactly.
  return z <= 1.01 ? null : Math.min(z, MAX_Z);
}

/* One decimal, and no trailing zero on a whole number: two presses of a 1.4 step
   land on 1.9599999, which is 2x and should read as it. */
function label(z: number): string {
  const shown = Math.round(z * 10) / 10;
  return `${Number.isInteger(shown) ? shown : shown.toFixed(1)}×`;
}

/**
 * Write the current view onto the live elements.
 *
 * Everything visible is derived here — the transform, the level, whether the
 * cursor says the photograph can be dragged, and which buttons have nothing left
 * to do — so no handler has to remember to keep any of it in step.
 */
function apply(stage: HTMLElement, eased: boolean): void {
  const img = imageIn(stage);
  if (!img) return;

  const limit = panLimit(stage, img, view.z);
  view.tx = clamp(view.tx, -limit.x, limit.x);
  view.ty = clamp(view.ty, -limit.y, limit.y);

  img.classList.toggle('photolb__img--eased', eased);
  img.style.transform = `translate(${view.tx.toFixed(1)}px, ${view.ty.toFixed(1)}px) scale(${view.z.toFixed(3)})`;
  stage.classList.toggle('photolb__stage--pannable', limit.x > 0.5 || limit.y > 0.5);

  const level = stage.querySelector<HTMLElement>('.zoomtb__level');
  if (level) level.textContent = label(view.z);

  const actual = oneToOne(img);
  const disable = (sel: string, off: boolean): void => {
    const btn = stage.querySelector<HTMLButtonElement>(sel);
    if (btn) btn.disabled = off;
  };
  disable('.zoomtb__out', view.z <= MIN_Z + 0.001);
  disable('.zoomtb__in', view.z >= MAX_Z - 0.001);
  disable('.zoomtb__fit', view.z <= MIN_Z + 0.001 && Math.abs(view.tx) < 0.5 && Math.abs(view.ty) < 0.5);
  // Nothing to reach: the fitted view is already this photograph's own pixels.
  disable('.zoomtb__actual', actual === null || Math.abs(view.z - actual) < 0.01);
}

/**
 * Zoom to `next`, keeping the point under `anchor` where it is.
 *
 * The anchor is in stage coordinates measured from the centre, which is where the
 * scale happens. Without it the wheel walks the detail you were looking at off
 * the edge of the frame — you zoom into a photograph to read one pier, not to
 * arrive at its middle.
 */
function zoomTo(
  stage: HTMLElement,
  next: number,
  anchor: { x: number; y: number } | null,
  eased: boolean,
): void {
  const z = clamp(next, MIN_Z, MAX_Z);
  if (anchor && view.z > 0) {
    view.tx = anchor.x - (anchor.x - view.tx) * (z / view.z);
    view.ty = anchor.y - (anchor.y - view.ty) * (z / view.z);
  }
  view.z = z;
  if (z <= MIN_Z + 0.001) {
    view.tx = 0;
    view.ty = 0;
  }
  apply(stage, eased);
}

function endDrag(stage: HTMLElement | null): void {
  drag = null;
  stage?.classList.remove('photolb__stage--dragging');
}

/** Pointer position relative to the centre of the stage — the scale's origin. */
function anchorAt(stage: HTMLElement, e: { clientX: number; clientY: number }): { x: number; y: number } {
  const box = stage.getBoundingClientRect();
  return { x: e.clientX - box.left - box.width / 2, y: e.clientY - box.top - box.height / 2 };
}

/**
 * One toolbar button. `off` is its state as the tree is built, from what the view
 * alone can say; `apply` corrects the ones that need the image measured — whether
 * 1:1 is anywhere to go — as soon as there is anything to measure.
 */
function toolbarButton(
  cls: string,
  title: string,
  path: IconPath,
  off: boolean,
  press: (stage: HTMLElement) => void,
): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: ['zoomtb__btn', cls],
      title,
      'aria-label': title,
      disabled: off,
      onclick: (e: Event) => {
        const stage = stageOf(e);
        if (stage) press(stage);
      },
    },
    icon(path, { size: 15, stroke: 'currentColor' }),
  );
}

/**
 * The photograph, in a frame it can be moved around inside.
 *
 * `alt` is the description the record already holds for this photograph — the
 * component and the inspector's note — and is what a screen reader gets. The
 * frame itself is focusable and says what the keys do, because dragging with a
 * mouse cannot be the only way to reach the far corner of a zoomed image.
 */
export function photoStage(src: string, alt: string): HTMLElement {
  /* A stage already on the document means this build is a repaint of an open
     dialog, so the view it is holding carries over. None means a dialog is
     opening: a photograph opens whole, however the last one was left. */
  const repaint = view.src === src && document.querySelector('.photolb__stage') !== null;
  if (!repaint) view = { src, z: MIN_Z, tx: 0, ty: 0 };

  const fitted = view.z <= MIN_Z + 0.001 && Math.abs(view.tx) < 0.5 && Math.abs(view.ty) < 0.5;

  /* Everything the tree cannot know without measuring — how far the pan may go on
     this window, whether 1:1 is anywhere to go — settled once this build has
     landed on the document. A microtask rather than a frame: the patch is
     synchronous, so by the time this runs the elements are the live ones, and it
     is over before anything is painted. It is also what re-clamps a pan that a
     resized window has made too far. */
  queueMicrotask(() => {
    const live = document.querySelector<HTMLElement>('.photolb__stage');
    if (live) apply(live, false);
  });

  const img = h('img', {
    class: 'photolb__img',
    src,
    alt,
    /* The browser's own image drag would take over the pan the moment the
       pointer moved, and drop a copy of the photograph onto whatever it was
       released over. */
    draggable: 'false',
    style: `transform:translate(${view.tx.toFixed(1)}px, ${view.ty.toFixed(1)}px) scale(${view.z.toFixed(3)})`,
    /* Until the bytes are decoded there is no natural size, so nothing knows
       whether 1:1 is reachable or how far the image may be panned. */
    onload: (e: Event) => {
      const stage = (e.currentTarget as HTMLElement).closest<HTMLElement>('.photolb__stage');
      if (stage) apply(stage, false);
    },
  });

  const toolbar = h(
    'div',
    { class: 'zoomtb', role: 'group', 'aria-label': 'Zoom' },
    toolbarButton('zoomtb__out', 'Zoom out (−)', ICONS.zoomOut, fitted, (stage) =>
      zoomTo(stage, view.z / STEP, null, true),
    ),
    h('span', {
      class: 'zoomtb__level',
      title: 'Zoom, with 1× the whole photograph in frame',
      text: label(view.z),
    }),
    toolbarButton('zoomtb__in', 'Zoom in (+)', ICONS.zoomIn, view.z >= MAX_Z - 0.001, (stage) =>
      zoomTo(stage, view.z * STEP, null, true),
    ),
    h('span', { class: 'zoomtb__sep' }),
    toolbarButton('zoomtb__fit', 'Fit the whole photograph (0)', ICONS.fit, fitted, (stage) =>
      zoomTo(stage, MIN_Z, null, true),
    ),
    /* Disabled until the bytes are decoded: there is nowhere to go 1:1 to until
       the photograph has a natural size, and the pass below turns it on. */
    toolbarButton('zoomtb__actual', 'Actual pixels (1:1)', ICONS.pixels, true, (stage) => {
      const shown = imageIn(stage);
      const actual = shown ? oneToOne(shown) : null;
      if (actual) zoomTo(stage, actual, null, true);
    }),
  );

  return h(
    'div',
    {
      class: 'photolb__stage',
      tabindex: '0',
      'aria-label': `${alt} — scroll to zoom, drag to move, arrow keys to move, 0 to fit`,

      /* Never scrolls the register behind the dialog: inside this frame the wheel
         belongs to the photograph. deltaMode 0 is pixels from a trackpad, 1 is
         lines from a wheel — a notch of the latter is worth one whole step, and
         pixels are ramped so a flick of a trackpad does not cross the whole range. */
      onwheel: (e: Event) => {
        const we = e as WheelEvent;
        const stage = stageOf(e);
        if (!stage) return;
        we.preventDefault();
        const notches = we.deltaMode === 0 ? we.deltaY / 240 : we.deltaY;
        zoomTo(stage, view.z * STEP ** -notches, anchorAt(stage, we), false);
      },

      /* Held down and moved is how anyone looks around a photograph, so the left
         button drags it — `button === 0`, which is the left button on a mouse and
         the first contact of a touch or a pen; the others are the menu and the
         paste, and neither is a pan.

         Nothing is preventDefault-ed here on purpose. Cancelling a pointerdown
         suppresses the mouse events the browser fires after it, and dblclick is
         one of them — the double-click that zooms back out would stop working the
         moment the photograph became draggable. What a native image drag and a
         text selection would otherwise do is turned off in CSS instead, on
         .photolb__stage and by draggable="false" on the image. */
      onpointerdown: (e: Event) => {
        const pe = e as PointerEvent;
        const stage = stageOf(e);
        // The toolbar's own buttons are not a place to start a drag from.
        if (!stage || pe.button !== 0 || (pe.target as HTMLElement).closest('.zoomtb')) return;
        stage.focus({ preventScroll: true });
        const shown = imageIn(stage);
        if (!shown) return;
        /* Nothing overhangs the frame, so there is nothing to look around at:
           the whole photograph is already in view and dragging it would only
           slide it off its own centre. The cursor says so — .photolb__stage
           only offers a grab hand when there is somewhere to go. */
        const limit = panLimit(stage, shown, view.z);
        if (limit.x < 0.5 && limit.y < 0.5) return;
        drag = { id: pe.pointerId, x: pe.clientX, y: pe.clientY };
        /* Capture, so a hand that leaves the frame mid-drag keeps dragging and
           lets go where it means to rather than where the frame ends. */
        stage.setPointerCapture?.(pe.pointerId);
        stage.classList.add('photolb__stage--dragging');
      },

      onpointermove: (e: Event) => {
        const pe = e as PointerEvent;
        const stage = stageOf(e);
        if (!stage || !drag || drag.id !== pe.pointerId) return;
        /* A move with no button held is a release this page never saw — the
           pointer went up over browser chrome, or another window took the focus.
           Without this the photograph follows the cursor around afterwards. */
        if (pe.buttons === 0) {
          endDrag(stage);
          return;
        }
        view.tx += pe.clientX - drag.x;
        view.ty += pe.clientY - drag.y;
        drag = { id: pe.pointerId, x: pe.clientX, y: pe.clientY };
        apply(stage, false);
      },

      onpointerup: (e: Event) => endDrag(stageOf(e)),
      onpointercancel: (e: Event) => endDrag(stageOf(e)),
      // Capture lost some other way — a gesture the browser took over, or the
      // element going away under the pointer.
      onlostpointercapture: (e: Event) => endDrag(stageOf(e)),

      /* Double-click is the shortcut people try first: all the way in on the spot
         they hit, and all the way back out from there. */
      ondblclick: (e: Event) => {
        const me = e as MouseEvent;
        const stage = stageOf(e);
        if (!stage || (me.target as HTMLElement).closest('.zoomtb')) return;
        const shown = imageIn(stage);
        const actual = shown ? oneToOne(shown) : null;
        const close = actual ?? 2;
        zoomTo(stage, view.z > MIN_Z + 0.001 ? MIN_Z : close, anchorAt(stage, me), true);
      },

      onkeydown: (e: Event) => {
        const ke = e as KeyboardEvent;
        const stage = stageOf(e);
        if (!stage) return;
        // Esc stays the dialog's: it closes the photograph, at any zoom.
        const step = ke.shiftKey ? PAN_KEY * 3 : PAN_KEY;
        switch (ke.key) {
          case '+':
          case '=':
            zoomTo(stage, view.z * STEP, null, true);
            break;
          case '-':
          case '_':
            zoomTo(stage, view.z / STEP, null, true);
            break;
          case '0':
            zoomTo(stage, MIN_Z, null, true);
            break;
          case 'ArrowLeft':
            view.tx += step;
            apply(stage, true);
            break;
          case 'ArrowRight':
            view.tx -= step;
            apply(stage, true);
            break;
          case 'ArrowUp':
            view.ty += step;
            apply(stage, true);
            break;
          case 'ArrowDown':
            view.ty -= step;
            apply(stage, true);
            break;
          default:
            return;
        }
        ke.preventDefault();
      },
    },
    img,
    toolbar,
  );
}
